import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {IResponse} from '@interfaces/response';
import {
  IAdvertiserDeviceAvailability,
  IAdvertiserDeviceCampaignInfo,
  IDeviceAdvertiser,
  IDeviceAdvertiserCampaign,
  IDeviceAdvertiserSingle
} from '@interfaces/advertiser/devices';
import {AdvertiserDevicesGetParamsType, AdvertiserDevicesSortType} from '../../types/advertiser-devices.type';
import {HttpClient, HttpResponseBase} from '@angular/common/http';
import {ErrorService} from '@services/error/error.service';
import {createHttpParams} from '@shared/functions';
import {catchError, share} from 'rxjs/operators';
import {removeEmptyPropsFromObj} from '@shared/functions';
import {OrderType} from '../../types/order.type';
import {IDeviceActivity} from '../../models/device-activity.model';
import {IPayload} from '@interfaces/payload';


export type favoriteDevicesGetParamsType = Partial<Omit<AdvertiserDevicesGetParamsType, 'onlyFavorite'>>;

@Injectable({
  providedIn: 'root'
})

export class AdvertiserDevicesService {

  constructor(private http: HttpClient,
              private errorService: ErrorService) {
  }

  public getAdvertiserDevices(props: AdvertiserDevicesGetParamsType): Observable<IResponse<IDeviceAdvertiser>> {
    const params = createHttpParams({...removeEmptyPropsFromObj(props)});

    return this.http.get<IResponse<IDeviceAdvertiser>>('advertizer/devices', {params})
      .pipe(
        share(),
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
  }

  public getAdvertiserFavoriteDevices(props: favoriteDevicesGetParamsType): Observable<IResponse<IDeviceAdvertiser>> {
    const params = createHttpParams({...removeEmptyPropsFromObj({...props, onlyFavorite: true})});

    return this.http.get<IResponse<IDeviceAdvertiser>>('advertizer/devices', {params})
      .pipe(
        share(),
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
  }

  public addToFavorite(id: number): Observable<HttpResponseBase> {
    return this.http.post<HttpResponseBase>('advertizer/devices/favorite', {deviceId: id}, {observe: 'response'}).pipe(
      catchError((err) => {
        this.errorService.showError(err);
        return throwError(err);
      })
    );
  }

  public removeFromFavorite(id: number): Observable<any> {
    return this.http.delete<HttpResponseBase>(`advertizer/devices/favorite/${id}`, {observe: 'response'}).pipe(
      catchError((err) => {
        this.errorService.showError(err);
        return throwError(err);
      })
    );
  }

  public getAdvertiserDeviceById(deviceId: number): Observable<IDeviceAdvertiserSingle> {
    return this.http.get<IDeviceAdvertiserSingle>(`advertizer/devices/${deviceId}`).pipe(
      catchError((err) => {
        this.errorService.showError(err);
        return throwError(err);
      })
    );
  }

  // devices for creating campaign
  public getAdvertiserDevicesForCampaign(
    {
      limit,
      skip = 0,
      search,
      sort,
      order,
      mediaId,
      start,
      finish,
      campaignId
    }: {
      limit: number, skip: number, search: string,
      sort: AdvertiserDevicesSortType, order: OrderType, mediaId?: number, start?: string, finish?: string, campaignId?: number
    }): Observable<IResponse<IDeviceAdvertiserCampaign>> {

    const params = createHttpParams({
      ...removeEmptyPropsFromObj({
        limit, skip, search, sort, order, mediaId, start, finish, savedCampaignId: campaignId
      })
    });
    return this.http.get<IResponse<IDeviceAdvertiserCampaign>>('advertizer/campaigns/devices', {params})
      .pipe(
        catchError((err) => {
          this.errorService.showError(err, false);
          return throwError(err);
        })
      );
  }

  // selected devices
  public getAdvertiserSelectedDevices(
    {
      limit,
      skip,
      mediaId,
      start,
      finish,
      savedCampaignId
    }: {
      limit: number, skip: number, mediaId?: number, start?: string, finish?: string, savedCampaignId?: number
    }): Observable<IResponse<IDeviceAdvertiserCampaign>> {

    const params = createHttpParams({
      ...removeEmptyPropsFromObj({
        limit, skip, mediaId, start, finish, savedCampaignId, onlySaved: true
      })
    });

    return this.http.get<IResponse<IDeviceAdvertiserCampaign>>('advertizer/campaigns/devices', {params})
      .pipe(
        share(),
        catchError((err) => {
          this.errorService.showError(err, false);
          return throwError(err);
        })
      );
  }

  // This endpoint is used for refreshing specified in dIds array devices info which are selected for campaign creation.
  public getSelectedDevicesActualInfo(
    {
      mediaId,
      start,
      finish,
      savedCampaignId,
      dIds
    }: { mediaId: number, start: string, finish: string, savedCampaignId?: number, dIds: number[] })
    : Observable<IResponse<IDeviceAdvertiserCampaign>> {

    let params = createHttpParams({
      ...removeEmptyPropsFromObj({
        mediaId, start, finish, savedCampaignId
      })
    });
    dIds.forEach((id) => {
      params = params.append('dIds', id.toString());
    });

    return this.http.get<IResponse<IDeviceAdvertiserCampaign>>(`advertizer/campaigns/devices/refresh`, {params})
      .pipe(
        catchError((err) => {
          this.errorService.showError(err, false);
          return throwError(err);
        })
      );
  }

  public getCampaignDevices({limit, skip, campaignId, sort, order}
                              : { limit: number, skip: number, campaignId: number, order: OrderType, sort: AdvertiserDevicesSortType })
    : Observable<IResponse<IAdvertiserDeviceCampaignInfo>> {
    const params = createHttpParams({limit, skip, sort, order});
    return this.http.get<IResponse<IAdvertiserDeviceCampaignInfo>>(`advertizer/campaigns/${campaignId}/devices`, {params})
      .pipe(
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
  }

  public getCampaignDevicesActivity({campaignId, payloadData}:
                                      { campaignId: number; payloadData: IPayload<AdvertiserDevicesSortType> }):
    Observable<IResponse<IDeviceActivity>> {
    const params = createHttpParams({
      skip: payloadData.skip, limit: payloadData.limit, order: payloadData.order, sort: payloadData.sort
    });

    return this.http.get<IResponse<IDeviceActivity>>(`advertizer/campaigns/${campaignId}/devices/activity`, {params})
      .pipe(
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
  }

  public getDevicesAvailability({
                                  deviceIds, mediaId, start, finish
                                }: { deviceIds: number[], mediaId: number, start: string, finish: string })
    : Observable<IAdvertiserDeviceAvailability[]> {
    let params = createHttpParams({
      ...removeEmptyPropsFromObj({
        mediaId, start, finish
      })
    });
    deviceIds.forEach((id) => {
      params = params.append('dIds', id.toString());
    });

    return this.http.get<IAdvertiserDeviceAvailability[]>(`advertizer/campaigns/devices/availability`, {params})
      .pipe(
        catchError((err) => {
          this.errorService.showError(err, false);
          return throwError(err);
        })
      );
  }
}
